import Link from "next/link";
import { MegaMenuConfig } from "./types";
import FeaturedCard from "./featured-card";
import LinkGroup from "./link-group";
import { trackMegaMenuLinkClick } from "@/utils/analytics";
import { getCustomComponent } from "./custom";

interface MenuPanelProps {
  menu: MegaMenuConfig;
  isMobile?: boolean;
  /** Load featured-card images only after the panel has been opened/hovered. */
  loadMedia?: boolean;
}

export default function MenuPanel({
  menu,
  isMobile = false,
  loadMedia = false,
}: MenuPanelProps) {
  // Check if menu has a custom component
  const CustomComponent = getCustomComponent(menu.customComponent);
  
  if (CustomComponent) {
    return <CustomComponent menu={menu} />;
  }
  
  // Handle null/undefined linkGroups
  const linkGroups = menu.linkGroups || [];
  
  // Calculate how many columns we need for link groups (max 5)
  const linkGroupColumns = linkGroups.length > 0 ? Math.min(linkGroups.length, 5) : 0;
  
  // Distribute link groups across columns
  const linkGroupsPerColumn = linkGroupColumns > 0 ? Math.ceil(linkGroups.length / linkGroupColumns) : 0;
  const linkGroupColumnsData: typeof linkGroups[] = [];
  
  if (linkGroupColumns > 0) {
    for (let i = 0; i < linkGroupColumns; i++) {
      const start = i * linkGroupsPerColumn;
      const end = start + linkGroupsPerColumn;
      linkGroupColumnsData.push(linkGroups.slice(start, end));
    }
  }

  return (
    <div className="relative bg-white border-t border-gray-100 rounded-xl shadow-lg w-full">
      <div className="max-w-7xl px-6">
        {/* Helper text */}
        {menu.helperText && (
          <div className="pt-4 pb-4 border-b border-gray-100 flex items-start justify-between">
            <p className="text-sm text-gray-700 font-medium leading-relaxed max-w-3xl">
              {menu.helperText}
            </p>
            {menu.viewAll && (
              <Link
                href={menu.viewAll.url}
                className="inline-flex items-center text-sm font-semibold text-brand-600 hover:text-brand-700 transition-all duration-200 group/viewall ml-6 flex-shrink-0"
                onClick={() => {
                  trackMegaMenuLinkClick({
                    menu: menu.id,
                    linkText: menu.viewAll.label,
                    linkUrl: menu.viewAll.url,
                  });
                }}
              >
                <span>{menu.viewAll.label}</span>
                <svg
                  className="ml-2 h-4 w-4 transition-transform duration-200 group-hover/viewall:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            )}
          </div>
        )}
        
        {/* Main content grid - Link groups */}
        {linkGroups.length > 0 && (
          <div
            className="grid gap-x-6 gap-y-8 py-6"
            style={{
              gridTemplateColumns: `repeat(${linkGroupColumns}, minmax(0, 1fr))`,
              gridAutoRows: "min-content",
            }}
          >
            {/* Link groups - render by row instead of by column */}
            {Array.from({ length: linkGroupsPerColumn }).flatMap((_, rowIndex) =>
              linkGroupColumnsData.map((columnGroups, columnIndex) => {
                const group = columnGroups[rowIndex];
                if (!group) {
                  return <div key={`empty-${columnIndex}-${rowIndex}`} />;
                }
                return (
                  <LinkGroup
                    key={`${columnIndex}-${rowIndex}`}
                    group={group}
                    menuId={menu.id}
                    groupId={`group-${columnIndex}-${rowIndex}`}
                  />
                );
              })
            )}
          </div>
        )}
        
        {/* Featured cards at bottom - horizontal row */}
        {menu.featuredCards.length > 0 && (
          <div className="pt-6 pb-6 border-t border-gray-100">
            <div className="grid grid-cols-1 items-center gap-x-8 gap-y-10 lg:grid-cols-5">
              {menu.featuredCards.map((card, index) => (
                <FeaturedCard
                  key={index}
                  card={card}
                  menuId={menu.id}
                  loadMedia={loadMedia}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* View all link at bottom - only show if no helper text */}
        {!menu.helperText && menu.viewAll && (
          <div className="pb-6 pt-6 border-t border-gray-100">
            <Link
              href={menu.viewAll.url}
              className="inline-flex items-center text-sm font-semibold text-brand-600 hover:text-brand-700 transition-all duration-200 group/viewall"
              onClick={() => {
                trackMegaMenuLinkClick({
                  menu: menu.id,
                  linkText: menu.viewAll.label,
                  linkUrl: menu.viewAll.url,
                });
              }}
            >
              <span>{menu.viewAll.label}</span>
              <svg
                className="ml-2 h-4 w-4 transition-transform duration-200 group-hover/viewall:translate-x-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
